// Port of TrailData/Import/ImportAssembler.swift (ImportFeature and ImportAssembler).

import { userImportProvenance } from '../core/domain';
import { computeElevationStatistics, HikingTime } from '../core/elevation';
import { joinPolylines, polygonArea, polygonCentroid, polylineLength } from '../core/geo';
import type {
  Confidence,
  CountryCode,
  DataProvenance,
  EpochMs,
  GeoPolygon,
  POICategory,
  PointOfInterest,
  ProtectedArea,
  RoutePoint,
  TrailStage,
  Waypoint,
} from '../core/models';
import { TrailDataError } from '../data/errors';
import { type ImportedGeoData, importedGeoDataIsEmpty, makeImportedGeoData } from './importedGeoData';
import * as M from './propertyMapping';
import { trimWhitespace, trimWhitespaceAndNewlines } from './text';

/** Format-independent geometry and properties of one source feature (GeoJSON Feature, KML Placemark). */
export interface ImportFeature {
  /** Source identifier (GeoJSON `id`, KML `id` attribute), if any. */
  sourceID?: string;
  /** Properties with lower-cased keys. */
  properties: M.ImportProperties;
  points: RoutePoint[];
  lines: RoutePoint[][];
  polygons: GeoPolygon[];
}

export function makeImportFeature(sourceID: string | undefined, properties: M.ImportProperties): ImportFeature {
  const feature: ImportFeature = { properties, points: [], lines: [], polygons: [] };
  if (sourceID !== undefined) feature.sourceID = sourceID;
  return feature;
}

/** Normalises property keys to lower case (first occurrence in sorted key order wins) and trims values. */
export function normalizedProperties(raw: Readonly<Record<string, string>>): Record<string, string> {
  const out: Record<string, string> = Object.create(null) as Record<string, string>;
  for (const key of Object.keys(raw).sort()) {
    const k = trimWhitespaceAndNewlines(key).toLowerCase();
    const v = trimWhitespaceAndNewlines(raw[key]);
    if (k === '' || v === '' || Object.hasOwn(out, k)) continue;
    out[k] = v;
  }
  return out;
}

const confidenceRank: Record<Confidence, number> = { low: 0, medium: 1, high: 2 };

interface CollectedLine {
  name?: string;
  properties: M.ImportProperties;
  points: RoutePoint[];
}

/**
 * Turns `ImportFeature`s into domain records with deterministic identifiers.
 *
 * Identifiers are `import:<hash>` for the trail, `import:<hash>:<n>` for stages, and
 * `import:<hash>:poi|area|wpt:<key>` for other records, where `<hash>` is derived from the file content (so
 * re-importing the same file replaces its records when merged instead of duplicating them) and `<key>` is the
 * feature's own id when present, else its 1-based position.
 */
export class ImportAssembler {
  /**
   * ISO 3166 user-assigned code conventionally meaning "unknown region": imported geometry says nothing reliable about
   * jurisdiction, so no country-level regulation may be attached to it by accident.
   */
  static readonly unknownCountry: CountryCode = 'ZZ';

  readonly idPrefix: string;
  readonly baseName: string;
  readonly provenance: DataProvenance;
  /** Collection-level name (GeoJSON `name` member, KML `<Document><name>`), used for the trail. */
  collectionName?: string;

  private readonly result = makeImportedGeoData();
  private readonly lines: CollectedLine[] = [];
  private readonly usedIDs = new Set<string>();
  private featureCount = 0;

  /** `data`: the file content (its UTF-8 bytes are hashed for the identifiers). */
  constructor(data: string | Uint8Array, sourceName: string, importedAt: EpochMs, provenance?: DataProvenance) {
    this.idPrefix = `import:${ImportAssembler.contentHash(data)}`;
    this.baseName = ImportAssembler.baseName(sourceName);
    this.provenance = provenance ?? userImportProvenance(sourceName, importedAt);
  }

  warn(message: string): void {
    this.result.warnings.push(message);
  }

  // MARK: Features

  add(feature: ImportFeature): void {
    this.featureCount += 1;
    const key = ImportAssembler.sanitizedKey(feature.sourceID) ?? String(this.featureCount);
    const label = `feature ${key}`;
    const props = feature.properties;
    const name = M.name(props);
    const category = M.poiCategory(props);

    feature.points.forEach((raw, offset) => {
      const suffix = feature.points.length > 1 ? `-${offset + 1}` : '';
      const elevation = ImportAssembler.pointElevation(raw.elevation) ?? M.elevation(props);
      const point: RoutePoint = elevation === undefined ? { coordinate: raw.coordinate } : { coordinate: raw.coordinate, elevation };
      if (category) {
        this.addPOI(key + suffix, name, category, point, props);
      } else {
        this.warn(`${label}: no recognised POI category; imported as waypoint`);
        const waypoint: Waypoint = {
          id: this.uniqueID(`${this.idPrefix}:wpt:${key}${suffix}`),
          name: name ?? `${this.baseName} ${this.featureCount}${suffix}`,
          coordinate: point.coordinate,
          kind: { kind: 'imported' },
        };
        if (point.elevation !== undefined) waypoint.elevation = point.elevation;
        const note = M.value(props, 'description', 'note');
        if (note !== undefined) waypoint.note = note;
        this.result.waypoints.push(waypoint);
      }
    });

    const usableLines = feature.lines.filter((l) => l.length >= 2);
    if (usableLines.length < feature.lines.length) {
      this.warn(`${label}: ${feature.lines.length - usableLines.length} line(s) with fewer than 2 valid positions skipped`);
    }
    if (usableLines.length > 0) {
      const joined = joinPolylines(usableLines.map((points) => ({ points })));
      const line: CollectedLine = { properties: props, points: ImportAssembler.lineElevationsNormalized(joined.points) };
      if (name !== undefined) line.name = name;
      this.lines.push(line);
    }

    const usablePolygons = feature.polygons.filter((p) => p.exterior.length >= 3);
    if (usablePolygons.length < feature.polygons.length) {
      this.warn(`${label}: ${feature.polygons.length - usablePolygons.length} polygon(s) with fewer than 3 vertices skipped`);
    }
    if (usablePolygons.length > 0) {
      if (category && !M.indicatesProtectedArea(props)) {
        // A facility mapped as an area (e.g. a campsite outline): import it as a POI at its centroid.
        let largest = usablePolygons[0];
        for (const p of usablePolygons) if (polygonArea(p) > polygonArea(largest)) largest = p;
        const elevation = M.elevation(props);
        const coordinate = polygonCentroid(largest);
        this.addPOI(key, name, category, elevation === undefined ? { coordinate } : { coordinate, elevation }, props);
      } else {
        this.addProtectedArea(key, label, name, usablePolygons, props);
      }
    }
  }

  private addPOI(key: string, name: string | undefined, category: POICategory, point: RoutePoint, props: M.ImportProperties) {
    const poi: PointOfInterest = {
      id: this.uniqueID(`${this.idPrefix}:poi:${key}`),
      name: name ?? `${this.baseName} ${this.featureCount}`,
      category,
      coordinate: point.coordinate,
      attributes: M.poiAttributes(category, props),
      provenance: this.provenance,
      confidence: this.cappedConfidence(M.confidence(props)),
    };
    if (point.elevation !== undefined) poi.elevation = point.elevation;
    const country = M.countryCode(props);
    if (country !== undefined) poi.countryCode = country;
    this.result.pointsOfInterest.push(poi);
  }

  private addProtectedArea(
    key: string,
    label: string,
    name: string | undefined,
    polygons: GeoPolygon[],
    props: M.ImportProperties,
  ): void {
    const { rule, unrecognised } = M.overnightRule(props);
    if (unrecognised !== undefined) this.warn(`${label}: overnight_rule '${unrecognised}' not recognised; treated as unknown`);
    const areaName = name ?? `${this.baseName} ${this.featureCount}`;
    const country = M.countryCode(props) ?? ImportAssembler.unknownCountry;
    const area: ProtectedArea = {
      id: this.uniqueID(`${this.idPrefix}:area:${key}`),
      name: areaName,
      kind: M.protectedAreaKind(props),
      polygons,
      countryCode: country,
      jurisdiction: {
        level: 'protectedAreaAuthority',
        countryCode: country,
        name: M.value(props, 'operator', 'authority') ?? areaName,
      },
      overnightRule: rule,
      accessRule: M.accessRule(props),
      regulationIDs: M.regulationIDs(props),
      isSynthetic: this.provenance.isSynthetic,
      provenance: this.provenance,
      confidence: this.cappedConfidence(M.confidence(props)),
    };
    const season = M.seasonalWindow(props);
    if (season) area.seasonalWindow = season;
    this.result.protectedAreas.push(area);
  }

  /**
   * Imported data defaults to `low` confidence; synthetic data can never be more than `low`, and a file the user
   * imported (`userProvided`, unverified by anyone) can never claim more than `medium`, whatever it states.
   */
  private cappedConfidence(stated: Confidence | undefined): Confidence {
    if (this.provenance.isSynthetic) return 'low';
    const c = stated ?? 'low';
    if (this.provenance.licenseReviewStatus === 'userProvided' && confidenceRank[c] > confidenceRank.medium) return 'medium';
    return c;
  }

  // MARK: Result

  /** Builds the trail from collected lines and returns the result. Throws `TrailDataError` `noGeometry` when empty. */
  finish(): ImportedGeoData {
    const out: ImportedGeoData = {
      ...this.result,
      trails: [],
      stages: [],
      warnings: [...this.result.warnings],
    };
    if (this.lines.length > 0) {
      const trailID = this.idPrefix;
      const trailName = this.collectionName ?? (this.lines.length === 1 ? this.lines[0].name : undefined) ?? this.baseName;
      this.lines.forEach((line, index) => {
        const number = index + 1;
        const stageName = line.name ?? (this.lines.length === 1 ? trailName : `${trailName} ${number}`);
        out.stages.push(
          ImportAssembler.makeStage(`${trailID}:${number}`, trailID, number, stageName, line.points, line.properties, this.provenance),
        );
      });
      const countries = [...new Set(out.stages.map((s) => s.countryCode))]
        .filter((c) => c !== ImportAssembler.unknownCountry)
        .sort();
      out.trails = [
        {
          id: trailID,
          name: trailName,
          kind: 'imported',
          countries,
          regions: [],
          stageIDs: out.stages.map((s) => s.id),
          historicalNames: [],
          provenance: this.provenance,
        },
      ];
    }
    if (importedGeoDataIsEmpty(out)) throw TrailDataError.noGeometry();
    return out;
  }

  // MARK: Stages

  static makeStage(
    id: string,
    trailID: string,
    number: number,
    name: string,
    points: RoutePoint[],
    props: M.ImportProperties,
    provenance: DataProvenance,
  ): TrailStage {
    const polyline = { points };
    const stats = computeElevationStatistics(points.map((p) => p.elevation));
    const distance = polylineLength(polyline);
    const endpointNames = ImportAssembler.splitStageName(name);
    // Callers guarantee at least two points.
    const first = points[0];
    const last = points[points.length - 1];
    const place = (placeName: string, p: RoutePoint) =>
      p.elevation === undefined ? { name: placeName, coordinate: p.coordinate } : { name: placeName, coordinate: p.coordinate, elevation: p.elevation };
    const stage: TrailStage = {
      id,
      trailID,
      number,
      code: M.value(props, 'ref', 'code') ?? String(number),
      name,
      start: place(M.value(props, 'from') ?? endpointNames?.from ?? name, first),
      finish: place(M.value(props, 'to') ?? endpointNames?.to ?? name, last),
      countryCode: M.countryCode(props) ?? ImportAssembler.unknownCountry,
      polyline,
      distance,
      ascent: stats.ascent,
      descent: stats.descent,
      // TrailStage requires values; 0 stands for "unknown" when the line has no elevation.
      minElevation: stats.minElevation ?? 0,
      maxElevation: stats.maxElevation ?? 0,
      expectedDuration: HikingTime.estimate(distance, stats.ascent, stats.descent),
      difficulty: 'moderate',
      classifications: [],
      surfaces: [],
      segments: [],
      keyPoints: [],
      waterPOIIDs: [],
      settlementPOIIDs: [],
      accommodationPOIIDs: [],
      bailoutPOIIDs: [],
      notes: [],
      dataQuality: 'userImported',
      provenance,
    };
    const sac = M.sacScale(props);
    if (sac) stage.sacScale = sac;
    return stage;
  }

  /** "Kandersteg – Griesalp" / "Kandersteg - Griesalp" → { from: "Kandersteg", to: "Griesalp" }. */
  static splitStageName(name: string): { from: string; to: string } | undefined {
    for (const separator of [' – ', ' — ', ' - ']) {
      const parts = name.split(separator);
      if (parts.length === 2) {
        const from = trimWhitespace(parts[0]);
        const to = trimWhitespace(parts[1]);
        if (from !== '' && to !== '') return { from, to };
      }
    }
    return undefined;
  }

  // MARK: Elevation heuristics

  /**
   * A single point at exactly 0 m almost always means "no altitude" (KML clampToGround default, exporters padding the
   * third ordinate), so it is treated as unknown.
   */
  static pointElevation(value: number | undefined): number | undefined {
    return value === undefined || !Number.isFinite(value) || value === 0 ? undefined : value;
  }

  /** A line whose known elevations are all exactly 0 m carries no elevation information. */
  static lineElevationsNormalized(points: RoutePoint[]): RoutePoint[] {
    const known = points.flatMap((p) => (p.elevation === undefined ? [] : [p.elevation]));
    if (known.length === 0 || !known.every((e) => e === 0)) return points;
    return points.map((p) => ({ coordinate: p.coordinate }));
  }

  // MARK: Identifiers

  private uniqueID(base: string): string {
    let candidate = base;
    let n = 2;
    while (this.usedIDs.has(candidate)) {
      candidate = `${base}-${n}`;
      n += 1;
    }
    this.usedIDs.add(candidate);
    return candidate;
  }

  /** Feature ids reduced to a safe character set (letters, digits, `.`, `_`, `-`, `/`), at most 64 characters. */
  static sanitizedKey(raw: string | undefined): string | undefined {
    if (raw === undefined) return undefined;
    const key = Array.from(raw, (c) => (/^[A-Za-z0-9._\-/]$/.test(c) ? c : '_'))
      .slice(0, 64)
      .join('');
    return key === '' ? undefined : key;
  }

  /** First 8 hex digits of the 64-bit FNV-1a hash of the content (its UTF-8 bytes for text). */
  static contentHash(data: string | Uint8Array): string {
    const bytes = typeof data === 'string' ? new TextEncoder().encode(data) : data;
    // 64-bit FNV-1a on two 32-bit halves: offset basis 0xcbf29ce484222325, prime 0x100000001b3 = 2^40 + 0x1b3.
    let hi = 0xcbf29ce4;
    let lo = 0x84222325;
    for (let i = 0; i < bytes.length; i++) {
      lo = (lo ^ bytes[i]) >>> 0;
      const loTimes = lo * 0x1b3; // < 2^41, exact
      const carry = Math.floor(loTimes / 0x1_0000_0000);
      const newLo = loTimes >>> 0;
      // hi·0x1b3 + carry + (lo << 40 contributes lo's low 24 bits << 8 to the high word)
      hi = (hi * 0x1b3 + carry + ((lo << 8) >>> 0)) % 0x1_0000_0000;
      lo = newLo;
    }
    return hi.toString(16).padStart(8, '0');
  }

  /** Last path component without a known geodata extension. */
  static baseName(sourceName: string): string {
    const trimmed = trimWhitespaceAndNewlines(sourceName);
    const components = trimmed.split('/').filter((c) => c !== '');
    const last = components[components.length - 1] ?? trimmed;
    for (const ext of ['.geojson', '.json', '.kml', '.gpx']) {
      if (last.toLowerCase().endsWith(ext)) {
        const base = last.slice(0, last.length - ext.length);
        if (base !== '') return base;
      }
    }
    return last === '' ? 'Import' : last;
  }
}
